import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Robot } from '../labyrinth/Robot';

const MAX_DASHES = 256;
const Y_OFFSET   = 0.18;

const DASH_LEN_PX = TILE_SIZE * 0.5;
const GAP_LEN_PX  = TILE_SIZE * 0.3;
const CYCLE_LEN   = DASH_LEN_PX + GAP_LEN_PX;
const SCROLL_SPEED = TILE_SIZE * 2; // matches 2D drawRoute

/**
 * Animated dashed route line drawn flat on the floor. Mirrors
 * LabyrinthState.drawRoute including scrolling dashes, route-fade-with-distance,
 * and color/width based on whether this is a marker route or autonomous.
 *
 * Each dash is emitted as a quad; one merged BufferGeometry rebuilt each frame.
 */
export class Route3D {
  readonly group = new THREE.Group();
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly mesh: THREE.Mesh;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly indices: Uint16Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;

  constructor() {
    const maxVerts = MAX_DASHES * 4;
    this.positions = new Float32Array(maxVerts * 3);
    this.colors    = new Float32Array(maxVerts * 4);
    this.indices   = new Uint16Array(MAX_DASHES * 6);
    for (let d = 0; d < MAX_DASHES; d++) {
      const i = d * 4;
      const o = d * 6;
      this.indices[o]     = i;
      this.indices[o + 1] = i + 1;
      this.indices[o + 2] = i + 2;
      this.indices[o + 3] = i;
      this.indices[o + 4] = i + 2;
      this.indices[o + 5] = i + 3;
    }

    this.geometry = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.positions, 3);
    this.colAttr = new THREE.BufferAttribute(this.colors, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('color', this.colAttr);
    this.geometry.setIndex(new THREE.BufferAttribute(this.indices, 1));
    this.geometry.setDrawRange(0, 0);

    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      vertexColors: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 2;
    this.mesh.position.y = Y_OFFSET;
    this.group.add(this.mesh);
  }

  sync(robot: Robot, frameTime: number): void {
    const points = robot.getPathPoints(0.4);
    if (points.length < 2) {
      this.geometry.setDrawRange(0, 0);
      return;
    }

    const isMarkerRoute = robot.state === 'moving_to_marker' && !!robot.marker;
    const color = isMarkerRoute ? 0x44aaff : 0xcc3344;
    const cr = ((color >> 16) & 0xff) / 255;
    const cg = ((color >>  8) & 0xff) / 255;
    const cb =  (color        & 0xff) / 255;
    const width = (isMarkerRoute ? 2.5 : 1.5);
    const halfW = width / 2;
    const baseAlpha = isMarkerRoute ? 0.9 : 0.5;

    const scrollOffset = (frameTime * SCROLL_SPEED) % CYCLE_LEN;
    let accumulated = -scrollOffset;
    let dashCount = 0;
    const totalSegs = points.length - 1;

    const pushDash = (
      x0: number, y0: number, x1: number, y1: number,
      dirX: number, dirY: number, alpha: number,
    ): void => {
      if (dashCount >= MAX_DASHES) return;
      // Perpendicular in XZ plane (right-hand: rotate dir by 90°)
      const perpX = -dirY;
      const perpY =  dirX;
      const off = dashCount * 4 * 3;
      const offC = dashCount * 4 * 4;

      const ax = x0 + perpX * halfW, az = y0 + perpY * halfW;
      const bx = x1 + perpX * halfW, bz = y1 + perpY * halfW;
      const cx = x1 - perpX * halfW, cz = y1 - perpY * halfW;
      const dx = x0 - perpX * halfW, dz = y0 - perpY * halfW;

      this.positions[off    ] = ax; this.positions[off + 1] = 0; this.positions[off + 2] = az;
      this.positions[off + 3] = bx; this.positions[off + 4] = 0; this.positions[off + 5] = bz;
      this.positions[off + 6] = cx; this.positions[off + 7] = 0; this.positions[off + 8] = cz;
      this.positions[off + 9] = dx; this.positions[off +10] = 0; this.positions[off +11] = dz;

      for (let i = 0; i < 4; i++) {
        this.colors[offC + i * 4    ] = cr;
        this.colors[offC + i * 4 + 1] = cg;
        this.colors[offC + i * 4 + 2] = cb;
        this.colors[offC + i * 4 + 3] = alpha;
      }
      dashCount++;
    };

    for (let i = 0; i < totalSegs; i++) {
      const [px0, py0] = points[i]!;
      const [px1, py1] = points[i + 1]!;
      const wx0 = px0 * TILE_SIZE, wy0 = py0 * TILE_SIZE;
      const wx1 = px1 * TILE_SIZE, wy1 = py1 * TILE_SIZE;
      const segDx = wx1 - wx0;
      const segDy = wy1 - wy0;
      const segLen = Math.hypot(segDx, segDy);
      if (segLen < 0.01) continue;
      const dirX = segDx / segLen;
      const dirY = segDy / segLen;

      const routeFrac = i / totalSegs;
      const segAlpha = Math.max(0, baseAlpha - routeFrac * 0.5);

      let segPos = 0;
      let safety = 0;
      while (segPos < segLen && safety++ < 4096) {
        const cyclePos = ((accumulated + segPos) % CYCLE_LEN + CYCLE_LEN) % CYCLE_LEN;
        const inDash = cyclePos < DASH_LEN_PX;
        if (inDash) {
          const dashRem = DASH_LEN_PX - cyclePos;
          const segRem  = segLen - segPos;
          const drawLen = Math.min(dashRem, segRem);
          const sx = wx0 + dirX * segPos;
          const sy = wy0 + dirY * segPos;
          const ex = wx0 + dirX * (segPos + drawLen);
          const ey = wy0 + dirY * (segPos + drawLen);
          pushDash(sx, sy, ex, ey, dirX, dirY, segAlpha);
          segPos += Math.max(drawLen, 0.001);
        } else {
          const gapRem = CYCLE_LEN - cyclePos;
          const segRem = segLen - segPos;
          segPos += Math.max(Math.min(gapRem, segRem), 0.001);
        }
      }
      accumulated += segLen;
    }

    this.posAttr.needsUpdate = true;
    this.colAttr.needsUpdate = true;
    this.geometry.setDrawRange(0, dashCount * 6);
  }

  dispose(): void {
    this.group.remove(this.mesh);
    this.material.dispose();
    this.geometry.dispose();
  }
}
